import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  cloneElement,
  KeyboardEvent,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { validateDate } from "../../../../app/common";

import theme from "../../../../theme";
import { RoundedButton } from "../../../components/styled/styled";
import { stringToList } from "../../../helpers/common";
import { isSearchTermsFilter } from "../../../helpers/filters";
import {
  fromIsoDateString,
  isoFormat,
  leadingZerosDateFormat,
} from "../../../helpers/formatDateTime";
import isEmpty from "../../../helpers/isEmpty";
import {
  CollapseMenu,
  CollapseMenuContent,
  FilterChipContainer,
  SearchBarContainer,
  SearchTermItem,
} from "./styled";

type Props = {
  label?: string;
  sx?: SxProps;
  value: string;
  onChange?: (v: string) => void;
  onBlur?: (v: string) => void;
  error?: { state: boolean; message: string };
  hideSearchHistory?: boolean;
  hideDeleteAllFilters?: boolean;
  hideReturnDefaultFilters?: boolean;
  searchHistory: boolean;
  localStorageName?: string;
  selectFromHistory?: (prevTerm: string) => void;
  translation: string | string[];
  children: ReactElement;
  filters: SearchFilter[];
  updateFilters: (filters: SearchFilter[]) => void;
  isOpen: boolean;
  toggleMenu: () => void;
  fieldComponent?: ReactElement;
  width?: string;
  isLoading?: boolean;
  showFilterComponents?: boolean;
  filtersLocalStorageName?: string;
  defaultFilters?: SearchFilter[];
  onAddSearchTerms?: (terms: string) => void;
  debounceSearchMs?: number;
  inputTestId?: string;
  /** Make the expanded content part of the page layout instead of an overlay. */
  menuInFlow?: boolean;
  /** Content aligned with the search input, to the right of the field. */
  trailing?: ReactNode;
};

export type FormatValueType = string | number | Date;
export type FilterType = "text" | "date" | "number" | "dateRange";

export type SearchFilter = {
  // What is sent with the query
  name: string;
  value: any;
  // What is displayed in the filter chip
  displayName?: string;
  displayValue?: string;
  tooltip?: string;
  // Defines how displayed data is handled
  type?: FilterType;
  // All values without truncation for multiple select fields
  lookupValues?: { id: string; name: string }[];
};

const FilterSearchFieldComponent = ({
  label,
  sx,
  value,
  onBlur,
  onChange,
  error,
  hideSearchHistory,
  hideDeleteAllFilters = false,
  hideReturnDefaultFilters = false,
  searchHistory,
  localStorageName,
  selectFromHistory,
  translation,
  children,
  filters,
  updateFilters,
  isOpen,
  toggleMenu,
  fieldComponent,
  width,
  isLoading,
  showFilterComponents = true,
  filtersLocalStorageName,
  defaultFilters,
  onAddSearchTerms,
  debounceSearchMs,
  inputTestId,
  menuInFlow = false,
  trailing,
}: Props) => {
  const { t } = useTranslation(translation);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(isOpen);
  const [showPrevSearchTerms, setShowPrevSearchTerms] =
    useState<boolean>(false);
  const [prevSearchTerms, setPrevSearchTerms] = useState<string[]>([]);
  const [localValue, setLocalValue] = useState<string>(value);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputStyling = isOpen
    ? { borderBottomRightRadius: "0px", borderBottomLeftRadius: "0px" }
    : { borderBottomRightRadius: "4px", borderBottomLeftRadius: "4px" };

  const showPrevSearches = () => {
    if (!hideSearchHistory) {
      // close when user starts typing
      setShowPrevSearchTerms(!localValue);
      setIsMenuOpen(!localValue);
    }
  };

  const handleFilterMenuOpenClose = () => {
    setShowPrevSearchTerms(false);
    toggleMenu();
  };

  const clearFilter = useCallback(
    (filters: SearchFilter[], filterName: string): SearchFilter[] => {
      return filters.map((f) =>
        f.name === filterName
          ? { ...f, value: "", ...(f.displayValue && { displayValue: "" }) }
          : { ...f },
      );
    },
    [],
  );

  const deleteFilter = useCallback(
    (filter: SearchFilter) => {
      let newFilters = clearFilter(filters, filter.name);
      updateFilters(newFilters);
    },
    [filters, updateFilters, clearFilter],
  );

  const deleteSearchTerm = useCallback(
    (term: string, filter: SearchFilter) => {
      const terms = Array.isArray(filter.value)
        ? filter.value
        : stringToList(filter.value as string, ",");

      const updatedTerms = terms.filter((t: string) => t !== term);

      let newFilters = filters.map((f) => {
        if (isSearchTermsFilter(f)) {
          return !isEmpty(updatedTerms)
            ? { ...f, value: updatedTerms }
            : { ...f, value: "" };
        }
        return f;
      });

      if (isEmpty(updatedTerms)) {
        const searchTermsFilter = newFilters.find(isSearchTermsFilter);
        if (searchTermsFilter) {
          newFilters = clearFilter(newFilters, searchTermsFilter.name);
        }
      }

      updateFilters(newFilters);
    },
    [filters, updateFilters, clearFilter],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        onAddSearchTerms &&
        localValue.trim()
      ) {
        e.preventDefault();
        // Cancel pending debounced update so stale value doesn't reapply
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        onAddSearchTerms(localValue);
        // Clear input immediately after add
        setLocalValue("");
        // Sync parent state to avoid debounce race restoring old value
        if (onChange && value !== "") {
          onChange("");
        }
      }
    },
    [onAddSearchTerms, localValue, onChange, value],
  );

  const handleSearchClick = useCallback(() => {
    if (onAddSearchTerms && localValue.trim()) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      onAddSearchTerms(localValue);
      setLocalValue("");
      if (onChange && value !== "") {
        onChange("");
      }
    }
  }, [onAddSearchTerms, localValue, onChange, value]);

  const deleteAllFilters = () => {
    const newFilters: SearchFilter[] = filters.map((f) => ({
      ...f,
      value: Array.isArray(f.value) ? [] : "",
      ...(f.displayValue && { displayValue: "" }),
    }));
    updateFilters(newFilters);
  };

  const returnDefaultFilters = () => {
    if (!defaultFilters || !filtersLocalStorageName) return;
    updateFilters(defaultFilters);
    localStorage.removeItem(filtersLocalStorageName);
  };

  const filterDuplicatesFromArray = (array: string[]) =>
    array.filter((item, index) => array.indexOf(item) === index);

  const saveSearchTerm = useCallback(() => {
    if (searchHistory && localStorageName && localValue !== "") {
      const fromLocalStorage = localStorage.getItem(localStorageName);
      const prevTerms: string[] = fromLocalStorage
        ? JSON.parse(fromLocalStorage)
        : [];
      const fiveLatestSearchTerms = filterDuplicatesFromArray([
        localValue,
        ...prevTerms,
      ]).slice(0, 5);
      localStorage.setItem(
        localStorageName,
        JSON.stringify(fiveLatestSearchTerms),
      );
      setPrevSearchTerms(fiveLatestSearchTerms);
    }
  }, [localValue, searchHistory, localStorageName]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      saveSearchTerm();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [saveSearchTerm]);

  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  // Sync value prop to local state
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounce local value changes to parent
  useEffect(() => {
    const debounceDelayMs = debounceSearchMs ?? 400;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (onChange && localValue !== value) {
        onChange(localValue);
      }
    }, debounceDelayMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localValue, onChange, value, debounceSearchMs]);

  // Search for initial prev search terms
  useEffect(() => {
    if (searchHistory && localStorageName) {
      const fromLocalStorage = localStorage.getItem(localStorageName);
      const items: string[] = fromLocalStorage && JSON.parse(fromLocalStorage);
      if (items) {
        setPrevSearchTerms(items);
      }
    }
  }, [searchHistory, localStorageName]);

  // TODO add types for eventually every filter that gets passed here, and
  // update this function to handle formatting more smartly
  const formatFilterValue = useCallback(
    (value: FormatValueType, type: FilterType | undefined) => {
      if (value === null || value === undefined) {
        return "";
      }

      if (type === "date") {
        const epochMilliseconds = Number(value);
        const dateValue = new Date(epochMilliseconds);
        // If dateValue is actual date, return date in dd.mm.yyyy format.
        // If dateValue is not actual date, return value through translation
        if (!isNaN(dateValue.getTime())) {
          const day = dateValue.getDate().toString().padStart(2, "0");
          const month = (dateValue.getMonth() + 1).toString().padStart(2, "0");
          const year = dateValue.getFullYear().toString();
          return `${day}.${month}.${year}`;
        } else if (validateDate(value as any, isoFormat)) {
          // Check for ISO date format "yyyy-MM-dd"
          return fromIsoDateString(value as string, leadingZerosDateFormat);
        } else {
          return t(value.toString());
        }
      } else if (type === "number" || type === "text") {
        return value;
      } else {
        return t(value.toString());
      }
    },
    [t],
  );

  const filterChips = useMemo(() => {
    const chips: ReactElement[] = [];
    filters.forEach((filter) => {
      // Special handling for searchTerms filter
      if (isSearchTermsFilter(filter) && filter.value) {
        const terms = Array.isArray(filter.value)
          ? filter.value
          : stringToList(filter.value as string, ",");

        terms.forEach((term: string, index: number) => {
          const filterLabel = `${filter.displayName || t("common:searchTerm")}: ${term}`;
          const key = `searchTerms-${index}-${term}`;
          chips.push(
            <Chip
              id={key}
              key={key}
              label={filterLabel}
              variant="filled"
              onDelete={() => {
                deleteSearchTerm(term, filter);
              }}
            />,
          );
        });
      } else {
        const filterLabel = `${t(filter.displayName || filter.name)}: ${
          filter.displayValue
            ? Array.isArray(filter.displayValue)
              ? filter.displayValue.map((val) => t(val)).join(", ")
              : formatFilterValue(filter.displayValue, filter.type)
            : Array.isArray(filter.value)
              ? filter.value.map((val) => t(val)).join(", ")
              : formatFilterValue(filter.value, filter.type)
        }`;
        if (filter.value && !!filter.value.toString()) {
          chips.push(
            filter.tooltip ? (
              <Tooltip title={filter.tooltip}>
                <Chip
                  key={filter.name}
                  label={filterLabel}
                  variant="filled"
                  onDelete={() => {
                    deleteFilter(filter);
                  }}
                />
              </Tooltip>
            ) : (
              <Chip
                key={filter.name}
                label={filterLabel}
                variant="filled"
                onDelete={() => {
                  deleteFilter(filter);
                }}
              />
            ),
          );
        }
      }
    });
    return chips;
  }, [filters, deleteFilter, deleteSearchTerm, t, formatFilterValue]);

  const withActionFieldComponent =
    fieldComponent &&
    cloneElement(fieldComponent, {
      actionIcon: (
        <IconButton onClick={() => handleFilterMenuOpenClose()}>
          {!isMenuOpen ? (
            <TuneIcon sx={{ width: "22px", height: "22px" }} />
          ) : (
            <KeyboardArrowUpIcon sx={{ width: "22px", height: "22px" }} />
          )}
        </IconButton>
      ),
    });

  return (
    <>
      <Box display="flex" alignItems="flex-start">
        <SearchBarContainer width={width}>
          {withActionFieldComponent || (
            <TextField
              autoComplete="off"
              sx={{
                ...sx,
              }}
              InputLabelProps={{ shrink: false }}
              value={localValue}
              onChange={(e) => {
                setLocalValue(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              fullWidth
              onBlur={(e) => {
                // Sync immediately on blur
                if (onChange && localValue !== value) {
                  onChange(localValue);
                }
                onBlur && onBlur(e.target.value);
                setIsMenuOpen(false);
              }}
              onFocus={showPrevSearches}
              InputProps={{
                style: {
                  border: "none",
                  backgroundColor: "#FFFFFF",
                  marginBottom: "20px",
                  boxShadow: "0px 4px 6px #1018281A",
                  ...inputStyling,
                },
                endAdornment: showFilterComponents ? (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {onAddSearchTerms && (
                      <RoundedButton
                        variant="contained"
                        onClick={handleSearchClick}
                        disabled={!localValue.trim() || isLoading}
                        size="small"
                      >
                        {t("common:search")}
                      </RoundedButton>
                    )}
                    <InputAdornment
                      position="end"
                      onClick={() => handleFilterMenuOpenClose()}
                    >
                      <IconButton>
                        {!isMenuOpen ? <TuneIcon /> : <KeyboardArrowUpIcon />}
                      </IconButton>
                    </InputAdornment>
                  </Box>
                ) : null,
              }}
              inputProps={{
                maxLength: 128,
                placeholder: label,
                ...(inputTestId ? { "data-testid": inputTestId } : {}),
              }}
              error={error?.state}
              helperText={error?.state && error.message}
            />
          )}
          <CollapseMenu
            in={isMenuOpen}
            sx={menuInFlow ? { position: "relative" } : undefined}
          >
            {showPrevSearchTerms && !localValue ? (
              <CollapseMenuContent>
                <Typography color={theme.palette.primary.light}>
                  {t("common:latestSearches").toUpperCase()}
                </Typography>
                {prevSearchTerms
                  ? prevSearchTerms.map((term) => (
                      <SearchTermItem
                        key={term}
                        // Need to use onMouseDown event instead of onClick so menu is not closed before click is registered.
                        onMouseDown={() => {
                          setLocalValue(term);
                          if (onChange) {
                            onChange(term);
                          }
                          return !!selectFromHistory
                            ? selectFromHistory(term)
                            : undefined;
                        }}
                      >
                        {term}
                      </SearchTermItem>
                    ))
                  : null}
              </CollapseMenuContent>
            ) : null}
            {!showPrevSearchTerms ? (
              <CollapseMenuContent>{children}</CollapseMenuContent>
            ) : null}
          </CollapseMenu>
        </SearchBarContainer>
        {trailing && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: 56,
              ml: 1.5,
              flexShrink: 0,
            }}
          >
            {trailing}
          </Box>
        )}
      </Box>
      {showFilterComponents && (
        <FilterChipContainer>
          {!isEmpty(filterChips) && (
            <>
              {filterChips.map((filterChip: any) => filterChip)}
              {!hideDeleteAllFilters && (
                <Chip
                  label={t("common:deleteAllFilters")}
                  onClick={deleteAllFilters}
                  variant="text"
                />
              )}
            </>
          )}
          {!hideReturnDefaultFilters &&
            defaultFilters &&
            filtersLocalStorageName && (
              <Chip
                label={t("common:returnDefaultFilters")}
                onClick={returnDefaultFilters}
                variant="text"
              />
            )}
        </FilterChipContainer>
      )}
    </>
  );
};

export const FilterSearchField = memo(FilterSearchFieldComponent);
